import { Button } from "components/Button";
import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router";
import { iconArrowRight } from "assets/icons";
import { asParams } from "utils/asParams";
import { BaseRoutes } from "routes/BaseRoutes";
import { CheckBox } from "components/CheckBox";
import { formattingDate, convertDateTimeToSendPOST } from "utils/formattingInputs";
import debounce from 'lodash.debounce';
import { FilterFieldsProps, FilterProps } from "components/TableModified/TableModifiedFilters";
import { useToast } from "hooks/useToast";
import { AuthConnectionErrorProps, useAuth } from "hooks/useAuth";
import { useHomeParams } from "hooks/usePagesParams/useHomeParams";

interface Property {
  name?: string,
  value?: string,
}

interface City {
  cityName: string,
  stateName: string,
}

interface Customer {
  customerId: number,
  name?: string,
  cpf?: string,
  phoneNumber?: string,
  email?: string,
  lastTimeLogged?: string,
  isConfi: boolean,
}

interface CustomerRowProps {
  name?: string,
  isConfi: JSX.Element,
  cpf?: string,
  lastTimeLogged?: string,
  email?: string,
  actions: JSX.Element[],
}

export const useCustomersTable = () => {
  const handleCityInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const eventValue = event.target.value;
    setCityTableFilters({
      ...cityTableFilters,
      searching: true,
      options: [],
    });
    getCities(eventValue).then((cities) => {
      setCityTableFilters({
        ...cityTableFilters,
        searching: false,
        options: cities.map((city) => {
          return {
            label: `${city.cityName} - ${city.stateName}`,
            value: `${city.cityName}:${city.stateName}`,
          }
        }),
      });
    }).catch((_) => {
      setCityTableFilters({
        ...cityTableFilters,
        searching: false,
        options: [],
      });
    });
  }
  const { params, setParams } = useHomeParams();
  const debouncedChangeHandler = useMemo(() => debounce(handleCityInputChange, 1000), []);
  
  const initialTableFilters:FilterProps = {
    'LastLoggedDay': {
      label: 'Último login',
      value: params?.LastLoggedDay ?? '',
      options: [
        {label: '7 dias', value: '7'},
        {label: '30 dias', value: '30'},
        {label: '90 dias', value: '90'},
      ],
    },

    'IsConfi': {
      label: 'Usuários verificados',
      value: params?.IsConfi ?? '',
      options: [
        {label: 'Não verificados', value: 'false'},
        {label: 'Verificados', value: 'true'},
      ],
    },

    'RegisterMinDate': {
      type: "datepicker",
      label: 'Cadastrado a partir de',
      value: params?.RegisterMinDate ?? '',
      showTimeSelect: true,
    },

    'RegisterMaxDate': {
      type: "datepicker",
      label: 'Cadastrado até',
      value: params?.RegisterMaxDate ?? '',
      showTimeSelect: true,
    },
  };

  const initialCityTableFilters:FilterFieldsProps = {
    label: 'Localidade',
    value: params?.City ?? '',
    search: true,
    searching: false,
    onSearchInputChange: debouncedChangeHandler,
    options: [],
  }
  
  const [properties, setProperties] = useState<Property[]>();
  const [customers, setCustomers] = useState<Customer[]>();
  const [customerRows, setCustomerRows] = useState<CustomerRowProps[]>([]);
  const [tableFilters, setTableFilters] = useState<FilterProps>(initialTableFilters);
  const [cityTableFilters, setCityTableFilters] = useState<FilterFieldsProps>(initialCityTableFilters);
  const [searchText, setSearchText] = useState<string>(params?.KeyWord ?? '');
  const [pageNumber, setPageNumber] = useState<number>(params?.PageNumber ?? 1);
  const [customersPerPage, setCustomersPerPage] = useState<number>(params?.RowsPerPage ?? 10);
  const [customersCount, setCustomersCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [refreshCount, setRefreshCount] = useState<number>(0);
  const searchTextFormRef = useRef<HTMLFormElement>(null);
  const history = useHistory();
  const { connectApi, cancelRequest } = useAuth();


  /* HOOKS */
  useEffect(() => {
    return () => {
      debouncedChangeHandler.cancel();
      cancelRequest();
    }
  }, []);

  
  useEffect(() => {
    setPageNumber(1);
  }, [
    tableFilters['LastLoggedDay'].value,
    tableFilters['IsConfi'].value,
    tableFilters['RegisterMinDate'].value,
    tableFilters['RegisterMaxDate'].value,
    cityTableFilters.value,
    searchText,
    customersPerPage
  ]);

  useEffect(() => {
    setParams?.({
      ...params,
      LastLoggedDay: tableFilters['LastLoggedDay']?.value ?? '',
      IsConfi: tableFilters['IsConfi']?.value ?? '',
      City: tableFilters['City']?.value ?? '',
      KeyWord: searchText,
      PageNumber: pageNumber,
      RowsPerPage: customersPerPage,
      RegisterMinDate: tableFilters['RegisterMinDate']?.value
        ? convertDateTimeToSendPOST(tableFilters['RegisterMinDate'].value)
        : '',
      RegisterMaxDate: tableFilters['RegisterMaxDate']?.value
        ? convertDateTimeToSendPOST(tableFilters['RegisterMaxDate'].value)
        : '',
    });
    getCustomers();
  }, [
    tableFilters['LastLoggedDay'].value,
    tableFilters['IsConfi'].value,
    tableFilters['RegisterMinDate'].value,
    tableFilters['RegisterMaxDate'].value,
    searchText,
    pageNumber,
    customersPerPage,
    cityTableFilters.value,
    refreshCount
  ]);

  /* HELPERS */
  const push = (route: string): void => {
    history.push(route);
  };

  const filtersAsParams = ():string => {
    var filters = [];
    const city = cityTableFilters.value?.split(':')[0] ?? '';
    const uf = cityTableFilters.value?.split(':')[1] ?? '';
    filters.push({key: 'LastLoggedDay', value: tableFilters['LastLoggedDay'].value ?? ''});
    filters.push({key: 'IsConfi', value: tableFilters['IsConfi'].value ?? ''});
    filters.push({key: 'City', value: city});
    filters.push({key: 'UF', value: uf});
    filters.push({key: 'KeyWord', value: searchText});
    filters.push({key: 'RegisterMinDate', value: tableFilters['RegisterMinDate'].value ? convertDateTimeToSendPOST(tableFilters['RegisterMinDate'].value) : ''});
    filters.push({key: 'RegisterMaxDate', value: tableFilters['RegisterMaxDate'].value ? convertDateTimeToSendPOST(tableFilters['RegisterMaxDate'].value) : ''});
    filters.push({key: 'PageNumber', value: pageNumber.toString()});
    filters.push({key: 'RowsPerPage', value: customersPerPage.toString()});
    return asParams(filters);
  };

  const rowActionButton = (route: string) => {
    return (
      <Button 
        label="" 
        size="small" 
        icon={iconArrowRight} 
        onlyIcon={true} 
        onClick={() => push(route)} 
      />
    )
  };

  const isConfiBadge = (isConfi: boolean) => {
    return (
      <CheckBox
        label={isConfi ? "Sim" : "Não"}
        name="isConfi"
        value=""
        readOnly={true}
        checked={true}
        checkedError={!isConfi}
      />
    )
  }

  /* EVENT HANDLERS */
  const handleSearchTextFormSubmit = (event: any) => {
    event.preventDefault();
    const text = event.target.searchText.value;
    setSearchText(text);
  }

  const handleFilterChange = (event: any, name?: string): void => {
    const eventName = name || event.target.name;
    const eventValue = name ? new Date(event) : event.target.value;
    if (eventName === 'City') {
      setCityTableFilters({
        ...cityTableFilters,
        value: eventValue,
      });
    } else {
      setTableFilters({
        ...tableFilters,
        [eventName]: {
          ...tableFilters[eventName],
          value: eventValue,
        }
      });
    }
  }

  const handleFilterRemove = (filterName: string): void => {
    if (filterName === 'City') {
      setCityTableFilters({
        ...cityTableFilters,
        value: '',
      });
    } else {
      setTableFilters({
        ...tableFilters,
        [filterName]: {
          ...tableFilters[filterName],
          value: '',
        }
      });
    }
  }

  const handleSearchTextRemove = ():void => {
    searchTextFormRef?.current?.reset();
    setSearchText('');
  }

  const handleClearFilters = ():void => {
    searchTextFormRef?.current?.reset();
    setSearchText('');
    setCityTableFilters({ ...cityTableFilters, value: '' });
    setTableFilters({
      ...tableFilters,
      'LastLoggedDay': { ...tableFilters['LastLoggedDay'], value: '' },
      'IsConfi': { ...tableFilters['IsConfi'], value: '' },
      'RegisterMinDate': { ...tableFilters['RegisterMinDate'], value: '' },
      'RegisterMaxDate': { ...tableFilters['RegisterMaxDate'], value: '' },
    });
    setRefreshCount(c => c + 1);
  }

  const handlePageSelected = (page: number):void => {
    setPageNumber(page);
  }

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLSelectElement>):void => {
    const eventName = event.target.name;
    const eventValue = event.target.value;
    setCustomersPerPage(parseInt(eventValue));
  }


  /* API CALLS */
  const getCustomers = async () => {
    setLoading(true);
    cancelRequest();
    connectApi({
      type: 'get',
      url: `customers?${filtersAsParams()}`,
      errorMessage: 'Falha ao listar usuários'
    }).then((response) => {
      setError(false);
      let responseData = response.data;
      let properties: Property[] = responseData.properties;
      let customers: Customer[] = responseData.customers;
      let customersCount:number = parseInt(properties?.find(p => p.name === 'Usuários encontrados')?.value ?? '0');
      setProperties(properties);
      setCustomers(customers);
      setCustomersCount(customersCount);
      setCustomerRows([
        ...customers.map((customer: Customer) => ({
          name: customer.name ?? '',
          isConfi: isConfiBadge(customer.isConfi),
          cpf: customer.cpf ?? '',
          lasTimeLogged: customer.lastTimeLogged ? formattingDate(customer.lastTimeLogged, true) : '',
          email: customer.email ?? '',
          actions: [rowActionButton(BaseRoutes.customerDetails.replaceAll(':id', customer.customerId.toString()))],
        })),
      ]);
      setLoading(false);
    }).catch((error: AuthConnectionErrorProps) => {
      if (error.errorType === 'connection') {
        setError(true);
        setLoading(false);
      }
    });
  }

  const getCities = async (keyWord: string): Promise<City[]> => {
    connectApi({
      type: 'get',
      url: `helper/city?keyWorld=${keyWord}`,
      errorMessage: 'Falha ao buscar cidade'
    }).then((response) => {
      const responseData:string[] = response.data;
      const cities:City[] = responseData.map((city) => {
        const [cityName, stateName] = city.split(',');
        return {
          cityName: cityName.trim(),
          stateName: stateName.trim(),
        }
      })
      return cities;
    });
    throw null;
  }

  return {
    loading,
    error,
    properties,
    customerRows,
    customersCount,
    searchText,
    tableFilters: {...tableFilters, ['City']: {...cityTableFilters}},
    searchTextFormRef,
    pageNumber,
    customersPerPage,
    params,
    handleSearchTextFormSubmit,
    handleFilterChange,
    handleFilterRemove,
    handleSearchTextRemove,
    handleClearFilters,
    handlePageSelected,
    handleRowsPerPageChange,
  };
}